import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { contactGroupsService, ContactGroup } from '@/services/contact-groups.service';
import { Plus, Users, Edit2, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

function GroupCard({ group, onEdit, onMembers, onDelete }: {
  group: ContactGroup;
  onEdit: () => void;
  onMembers: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:shadow-elevated">
      {/* Color strip header */}
      <div
        className="h-1.5 w-full"
        style={{ background: group.color }}
      />

      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
              style={{ background: `${group.color}18`, color: group.color }}
            >
              <Users size={17} />
            </div>
            <div className="min-w-0">
              <div className="truncate font-semibold text-text-primary">{group.name}</div>
              {group.description && (
                <div className="mt-0.5 truncate text-xs text-text-muted">{group.description}</div>
              )}
            </div>
          </div>

          <span
            className="shrink-0 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
            style={{ background: `${group.color}18`, color: group.color }}
          >
            {group.contactCount?.toLocaleString() ?? 0}
          </span>
        </div>

        <div className="mt-4 flex items-center gap-1.5 border-t border-border pt-3">
          <Button size="sm" variant="secondary" onClick={onMembers} className="flex-1">
            <Users size={13} className="mr-1" /> Members
          </Button>
          <Button size="sm" variant="ghost" onClick={onEdit} iconLeft={<Edit2 size={13} />}>
            Edit
          </Button>
          <button
            onClick={onDelete}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted hover:bg-danger-soft hover:text-danger transition-colors"
            aria-label="Delete group"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function GroupsList() {
  const [rows, setRows] = useState<ContactGroup[]>([]);
  const [del, setDel] = useState<ContactGroup | null>(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const refresh = () => {
    setLoading(true);
    contactGroupsService.list().then(setRows).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  return (
    <>
      <Topbar
        title="Groups"
        breadcrumb="Audience"
        description="Organize contacts into groups for campaigns and audience targeting."
        right={
          <Button onClick={() => nav('/groups/new')} iconLeft={<Plus size={14} />}>
            New group
          </Button>
        }
      />

      <div className="px-7 py-7 w-full">
        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="skeleton h-40 rounded-2xl" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-surface p-14 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-soft text-primary">
              <Users size={24} />
            </div>
            <div>
              <div className="font-semibold text-text-primary">No groups yet</div>
              <div className="mt-1 text-sm text-text-muted">Create a group to organize your contacts for campaigns.</div>
            </div>
            <Button onClick={() => nav('/groups/new')} iconLeft={<Plus size={14} />} className="mt-1">
              Create first group
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((r) => (
              <GroupCard
                key={r.groupUid}
                group={r}
                onEdit={() => nav(`/groups/${r.groupUid}`)}
                onMembers={() => nav(`/groups/${r.groupUid}/members`)}
                onDelete={() => setDel(r)}
              />
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!del}
        title="Delete group?"
        message={`Delete "${del?.name}"? Contacts will not be deleted.`}
        danger
        onConfirm={async () => {
          await contactGroupsService.remove(del!.groupUid);
          toast.success('Deleted');
          setDel(null);
          refresh();
        }}
        onCancel={() => setDel(null)}
      />
    </>
  );
}
